/**
 * CRMSyncPanel Component
 * Single Responsibility: Handle CRM synchronization and follow-up actions
 */

import React, { useState, useEffect } from 'react'
import { toast } from 'sonner'
import type { Task } from '../../../types/meeting'
import { postMeetingApi } from '../../../services/api/postMeetingApi'

const complianceScoreColor = (pct: number | null | undefined): string => {
  if (pct == null) return 'text-slate-500'
  if (pct >= 90) return 'text-green-600'
  if (pct >= 70) return 'text-amber-600'
  return 'text-red-600'
}

const sentimentColor = (mood: string | null | undefined): string => {
  switch (mood?.toLowerCase()) {
    case 'positive': return 'text-green-600'
    case 'neutral': return 'text-blue-600'
    case 'concerned': return 'text-amber-600'
    case 'negative': return 'text-red-600'
    default: return 'text-slate-500'
  }
}

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

interface CRMSyncPanelProps {
  // H1: pass the RM-reviewed subject/body so the sent email is exactly what was reviewed.
  onSendFollowUp: (email: string, subject: string, body: string) => void | Promise<void>
  onSyncCRM: () => void
  onFinish: () => void
  sessionId?: string
  clientName?: string
  clientEmail?: string
  meetingSummary?: string
  // Real meeting stats for the bottom-right panel. `null` renders as "—" — no
  // fabricated defaults. `tasks` drives the email body bullet list.
  durationLabel?: string
  complianceScorePct?: number | null
  clientMood?: string | null
  tasksCount?: number
  keyTopicsCount?: number | null
  tasks?: Task[]
  meetingType?: string
}

const CRMSyncPanel: React.FC<CRMSyncPanelProps> = ({
  onSendFollowUp,
  onSyncCRM,
  onFinish,
  sessionId,
  clientName = 'Client',
  clientEmail = '',
  meetingSummary = '',
  durationLabel,
  complianceScorePct = null,
  clientMood = null,
  tasksCount = 0,
  keyTopicsCount = null,
  tasks = [],
  // meetingType is still accepted (interface) but no longer used for the local template —
  // the draft now comes from the backend (H1).
}) => {
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>([
    'summary',
    'tasks'
  ])
  const [emailPreview, setEmailPreview] = useState({
    subject: '',
    body: ''
  })
  const [isEditingEmail, setIsEditingEmail] = useState(false)
  const [showFullPreview, setShowFullPreview] = useState(false)
  const [syncStatus, setSyncStatus] = useState<'idle' | 'pending' | 'success' | 'error'>('idle')

  // H1: seed the editable preview from the COMPLIANT backend draft (German + FinSA
  // disclaimer + RM signature), not a local English placeholder. The RM reviews/edits this
  // exact draft and it is sent verbatim — so what's reviewed is what's delivered. If the
  // draft can't be generated, the preview stays empty and Send is blocked (no fake send).
  const [draftLoading, setDraftLoading] = useState(true)
  useEffect(() => {
    let cancelled = false
    if (!sessionId) { setDraftLoading(false); return }
    setDraftLoading(true)
    postMeetingApi
      .generateFollowUpDraft({
        sessionId,
        recipientEmail: clientEmail,
        recipientName: clientName,
        summary: meetingSummary,
        actionItems: tasks.map(t => t.title).filter(Boolean),
      })
      .then(draft => {
        if (cancelled) return
        setEmailPreview({ subject: draft.email_subject, body: draft.email_content })
      })
      .catch(() => {
        if (cancelled) return
        setEmailPreview({ subject: '', body: '' })
        toast.error('Could not generate the follow-up draft — review is required before sending')
      })
      .finally(() => { if (!cancelled) setDraftLoading(false) })
    return () => { cancelled = true }
  }, [sessionId, clientName, clientEmail, meetingSummary, tasks])

  const documents = [
    { id: 'summary', label: 'Executive Summary', icon: '📝' },
    { id: 'tasks', label: 'Follow-up Tasks', icon: '📋' },
    { id: 'transcript', label: 'Meeting Transcript', icon: '📜' },
    { id: 'compliance', label: 'Compliance Report', icon: '✅' },
    { id: 'insights', label: 'AI Insights', icon: '🤖' }
  ]

  const handleDocumentToggle = (docId: string) => {
    setSelectedDocuments((prev) =>
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId]
    )
  }

  const handleSyncWithSelection = async () => {
    if (!sessionId) {
      toast.error('Session ID missing — cannot sync to CRM')
      return
    }
    if (selectedDocuments.length === 0) {
      toast.error('Select at least one document to sync')
      return
    }
    setSyncStatus('pending')
    try {
      // The selected documents drive the sync; target defaults to 'internal'
      // (Kielis client record). External CRM providers slot in backend-side.
      const result = await postMeetingApi.syncToCRM(sessionId, selectedDocuments)
      const n = result.synced.length
      if (n === 0) {
        // Nothing was actually persisted for the selection — don't fake success.
        setSyncStatus('error')
        toast.warning('Nothing to sync — the selected documents are not yet available')
        return
      }
      setSyncStatus('success')
      const missingNote = result.missing.length ? ` (${result.missing.length} not yet available)` : ''
      toast.success(`Synced ${n} document${n === 1 ? '' : 's'} to CRM${missingNote}`)
      onSyncCRM()
    } catch (err: any) {
      setSyncStatus('error')
      toast.error(err?.message || 'CRM sync failed')
    }
  }

  const handleEditEmail = () => {
    setIsEditingEmail(true)
    toast.info('Email editing mode enabled')
  }

  const handlePreviewEmail = () => {
    setShowFullPreview(!showFullPreview)
  }

  const handleSendEmail = () => {
    if (draftLoading) {
      toast.info('The draft is still being prepared…')
      return
    }
    if (!emailPreview.subject || !emailPreview.body) {
      toast.error('Email content is required')
      return
    }
    if (!clientEmail) {
      toast.error('Client email address is missing')
      return
    }
    // Send exactly what the RM reviewed/edited (H1) — no server-side regeneration.
    onSendFollowUp(clientEmail, emailPreview.subject, emailPreview.body)
  }

  const handleEmailContentChange = (field: 'subject' | 'body', value: string) => {
    setEmailPreview(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="space-y-6">
      {/* Email Preview Section */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-kielis-navy mb-4">
          ✉ Follow-up Email
        </h3>
        
        {/* Action Buttons */}
        <div className="flex gap-2 mb-4">
          <button 
            onClick={handleEditEmail}
            className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded text-sm hover:bg-slate-200 transition-all"
          >
            Edit
          </button>
          <button 
            onClick={handlePreviewEmail}
            className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded text-sm hover:bg-slate-200 transition-all"
          >
            {showFullPreview ? 'Collapse' : 'Preview'}
          </button>
          <button 
            onClick={handleSendEmail}
            className="px-3 py-1.5 bg-kielis-navy text-white rounded text-sm hover:bg-slate-800 transition-all"
          >
            Send
          </button>
        </div>
        
        {/* Email Content Display */}
        {isEditingEmail ? (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Subject:
              </label>
              <input
                type="text"
                value={emailPreview.subject}
                onChange={(e) => handleEmailContentChange('subject', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Body:
              </label>
              <textarea
                value={emailPreview.body}
                onChange={(e) => handleEmailContentChange('body', e.target.value)}
                rows={8}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-kielis-gold focus:border-transparent resize-none"
              />
            </div>
            <button
              onClick={() => setIsEditingEmail(false)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-all"
            >
              Done Editing
            </button>
          </div>
        ) : (
          <div className={`bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm ${
            showFullPreview ? '' : 'max-h-48 overflow-y-auto'
          }`}>
            <strong>Subject:</strong> {emailPreview.subject || 'Loading...'}
            <br /><br />
            <div className="whitespace-pre-wrap">
              {emailPreview.body || 'Generating email content...'}
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-kielis-navy mb-4">
          🚀 Quick Actions
        </h3>
        <div className="space-y-3">
          <button
            onClick={handleSyncWithSelection}
            disabled={syncStatus === 'pending'}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {syncStatus === 'pending' && '⏳ Syncing…'}
            {syncStatus === 'success' && '✅ Synced to CRM'}
            {syncStatus === 'error' && '⚠ Retry Sync to CRM'}
            {syncStatus === 'idle' && '📁 Sync to CRM'}
          </button>
        </div>
      </div>

      {/* Document Selection */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-kielis-navy mb-4">
          📄 Documents to Sync
        </h3>
        <div className="space-y-2">
          {documents.map((doc) => (
            <label
              key={doc.id}
              className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-all cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedDocuments.includes(doc.id)}
                onChange={() => handleDocumentToggle(doc.id)}
                className="w-4 h-4 text-kielis-navy border-slate-300 rounded focus:ring-2 focus:ring-kielis-gold"
              />
              <span className="text-lg">{doc.icon}</span>
              <span className="text-sm font-medium text-slate-700">
                {doc.label}
              </span>
            </label>
          ))}
        </div>
        <div className="mt-3 text-xs text-slate-500">
          {selectedDocuments.length} of {documents.length} selected
        </div>
      </div>

      {/* Meeting Stats — values derived from real meeting data; renders "—"
          when a metric is unavailable rather than fabricating one. */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-lg font-semibold text-kielis-navy mb-4">
          📊 Meeting Stats
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Duration</span>
            <span className="font-medium">{durationLabel || '—'}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Compliance Score</span>
            <span className={`font-medium ${complianceScoreColor(complianceScorePct)}`}>
              {complianceScorePct == null ? '—' : `${complianceScorePct}%`}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Client Sentiment</span>
            <span className={`font-medium ${sentimentColor(clientMood)}`}>
              {clientMood ? capitalize(clientMood) : '—'}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Tasks Created</span>
            <span className="font-medium">{tasksCount}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-600">Key Topics</span>
            <span className="font-medium">{keyTopicsCount ?? '—'}</span>
          </div>
        </div>
      </div>

      {/* Finish Button */}
      <button
        onClick={onFinish}
        className="w-full px-6 py-3 bg-kielis-navy text-white rounded-lg font-medium hover:bg-slate-800 transition-all"
      >
        ✅ Finish & Return to Dashboard
      </button>
    </div>
  )
}

export default CRMSyncPanel